import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/app/rootReducer";
import { AppDispatch } from "@/app/store";
import {
  fetchResources,
  fetchMoreResources,
  setResources,
  setPage,
  setHasMore,
  setLastFetchParams,
  setShowOnlyFavorites,
} from "../slices/newsSlice";
import { getFavoritesKey } from "@/api/getFavoritesKey/getFavoritesKeyApi";
import { toggleResourceFavorite } from "@/api/favoriteResource/favoriteResourceApi";
import { searchService } from "@/modules/hub/features/Search/services/searchService";

export function useNews() {
  const dispatch = useDispatch<AppDispatch>();

  const {
    resources,
    page,
    hasMore,
    totalCount,
    lastFetchParams,
    isFetching,
    showOnlyFavorites,
  } = useSelector((state: RootState) => state.newsV2);

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [favoriteUuids, setFavoriteUuids] = useState<Set<string>>(new Set());

  type SortOption = "newest" | "oldest" | "mostViewed" | "alphabetical";
  const [sortOption, setSortOption] = useState<SortOption>("newest");

  const sortMappings: Record<
    SortOption,
    { sortBy: string; sortOrder: string }
  > = {
    newest: { sortBy: "publishStartDate", sortOrder: "desc" },
    oldest: { sortBy: "publishStartDate", sortOrder: "asc" },
    mostViewed: { sortBy: "viewCount", sortOrder: "desc" },
    alphabetical: { sortBy: "title", sortOrder: "asc" },
  };

  const buildRequestData = useCallback(() => {
    const { sortBy, sortOrder } = sortMappings[sortOption];

    return {
      showFavorites: showOnlyFavorites || undefined,
      sortBy,
      sortOrder,
      page: 1,
      pageSize: 12,
    };
  }, [sortOption, showOnlyFavorites]);

  const isStateRestorable = useCallback(() => {
    if (!lastFetchParams) return false;

    const getFiltersWithoutPagination = (params: Record<string, unknown>) => {
      const paginationKeys = ["page", "pageSize"];
      return Object.fromEntries(
        Object.entries(params).filter(([key]) => !paginationKeys.includes(key)),
      );
    };

    const currentFilters = getFiltersWithoutPagination(buildRequestData());
    const previousFilters = getFiltersWithoutPagination(lastFetchParams);

    return JSON.stringify(currentFilters) === JSON.stringify(previousFilters);
  }, [lastFetchParams, buildRequestData]);

  // Fetch favorites on mount
  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const favoritesData = await getFavoritesKey("news");
        setFavoriteUuids(new Set(favoritesData.favoriteResourceUuids));
        searchService.updateFavoritesKey(
          favoritesData.favoritesSecuredApiKey,
          favoritesData.favoriteResourceUuids,
        );
      } catch (error) {
        console.error("[News] Error fetching favorites:", error);
      }
    };

    fetchFavorites();
  }, []);

  const refreshFavorites = useCallback(async () => {
    try {
      const favoritesData = await getFavoritesKey("news");
      setFavoriteUuids(new Set(favoritesData.favoriteResourceUuids));
      searchService.updateFavoritesKey(
        favoritesData.favoritesSecuredApiKey,
        favoritesData.favoriteResourceUuids,
      );
      return favoritesData;
    } catch (error) {
      console.error("[News] Error refreshing favorites:", error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const load = async () => {
      if (resources.length > 0 && lastFetchParams && isStateRestorable()) {
        setIsInitialLoading(false);
        return;
      }

      const request = buildRequestData();
      setLoading(true);
      dispatch(setResources([]));
      dispatch(setPage(1));
      dispatch(setHasMore(true));

      await dispatch(fetchResources(request));

      dispatch(setLastFetchParams(request));
      setLoading(false);
      setIsInitialLoading(false);
    };
    load();
  }, []);

  // Handle sort and filter changes
  useEffect(() => {
    if (isInitialLoading) return;

    const handleFilterChange = async () => {
      // If favorites filter is being turned ON, refresh favorites key first
      if (showOnlyFavorites) {
        try {
          const favoritesData = await getFavoritesKey("news");
          setFavoriteUuids(new Set(favoritesData.favoriteResourceUuids));
          searchService.updateFavoritesKey(
            favoritesData.favoritesSecuredApiKey,
            favoritesData.favoriteResourceUuids,
          );
        } catch (error) {
          console.error("[News] Failed to refresh favorites key:", error);
        }
      }

      const request = buildRequestData();
      setLoading(true);
      dispatch(setResources([]));
      dispatch(setPage(1));
      dispatch(setHasMore(true));
      await dispatch(fetchResources(request));
      dispatch(setLastFetchParams(request));
      setLoading(false);
    };

    handleFilterChange();
  }, [sortOption, showOnlyFavorites]);

  const fetchMore = useCallback(() => {
    const nextPage = page + 1;
    const request = { ...buildRequestData(), page: nextPage };
    dispatch(setPage(nextPage));
    dispatch(fetchMoreResources(request));
  }, [page, dispatch, buildRequestData]);

  const handleFavoriteToggle = useCallback(
    async (resourceUuid: string) => {
      const newFavorites = new Set(favoriteUuids);
      const wasInFavorites = newFavorites.has(resourceUuid);

      if (wasInFavorites) {
        newFavorites.delete(resourceUuid);
      } else {
        newFavorites.add(resourceUuid);
      }
      setFavoriteUuids(newFavorites);

      try {
        await toggleResourceFavorite({ resourceUuid });
        await refreshFavorites();

        // If showing only favorites and we unfavorited, refresh the list
        if (showOnlyFavorites && wasInFavorites) {
          const request = buildRequestData();
          dispatch(setResources([]));
          dispatch(setPage(1));
          dispatch(setHasMore(true));
          await dispatch(fetchResources(request));
        }
      } catch (error) {
        // Revert on error
        setFavoriteUuids(favoriteUuids);
        console.error("[News] Error toggling favorite:", error);
      }
    },
    [
      favoriteUuids,
      showOnlyFavorites,
      refreshFavorites,
      buildRequestData,
      dispatch,
    ],
  );

  const toggleFavoritesFilter = useCallback(() => {
    dispatch(setShowOnlyFavorites(!showOnlyFavorites));
  }, [dispatch, showOnlyFavorites]);

  // Merge favorite status into resources
  const resourcesWithFavorites = resources.map((resource) => ({
    ...resource,
    isFavorited: favoriteUuids.has(resource.uuid),
  }));

  return {
    resources: resourcesWithFavorites,
    page,
    hasMore,
    totalCount,
    loading: loading || isFetching,
    isInitialLoading,
    fetchMore,
    sortOption,
    setSortOption,
    showOnlyFavorites,
    toggleFavoritesFilter,
    handleFavoriteToggle,
  };
}
